import type { ComponentChildren } from 'preact'
import { useEffect, useRef } from 'preact/hooks'
import { Check, Pencil, Sparkles, TriangleAlert } from 'lucide-preact'
import type {
  BrandColorRoleViewDto,
  BrandTokenRole,
  BrandTokenSetViewDto,
  BrandTypeRoleViewDto,
  BrandValueRoleViewDto,
} from '@/types/knowledgeBase'
import { CopyButton } from '@/components/common/CopyButton'
import { Select } from '@/components/common/Select'
import { TextBox } from '@/components/common/TextBox'
import { BRAND_CLASSES } from '@/constants/theme'
import {
  COLOR_ROLES,
  TYPE_ROLES,
  VALUE_ROLES,
  brandTint,
  changedByLine,
  contrastGrade,
  contrastRatio,
  currentRoleValue,
  dayOf,
  formatRatio,
  isTypeRole,
  isValueRole,
  ownTokens,
  ownTypeGroups,
  problemText,
  removeRoleQuestion,
  roleEditorChanged,
  roleUsage,
  sizeOverLine,
  systemFallback,
  type RoleEditor,
} from '@/components/console/knowledgeBase/brandGuidelinesModel'
import type { FontLoad } from '@/components/console/knowledgeBase/useBrandFonts'
import {
  EYEBROW,
  GHOST_SMALL,
  H2,
  LEDE,
  QUIET_BUTTON,
  SAVE_SMALL,
  SWATCH_FRAME,
} from './brandGuidelinesStyles'

interface Props {
  set: BrandTokenSetViewDto | null
  canChange: boolean
  pending: boolean
  editor: RoleEditor | null
  fonts: Record<string, FontLoad>
  /** The entry form open elsewhere on the page, which keeps every role's Change closed until it is saved or cancelled. */
  entryWaiting: string | null
  onEdit: (role: BrandTokenRole) => void
  onEditorChange: (editor: RoleEditor) => void
  onCancel: () => void
  onSave: () => void
  onRemove: () => void
}

/**
 * The nine roles emails, landing pages and asset QA read, each changed in its own tile (ORB-904).
 *
 * ⚑ One editor is open at a time on the page, a role's or an entry's, and Confirm these colors in the page's header
 * waits for it: confirming stamps the set the person was shown, and a half-typed change is not part of it.
 */
export function BrandRoleTiles(props: Props) {
  const { set, canChange, editor } = props
  const roles = set?.Roles ?? null
  const background = roles?.Background.Hex ?? null
  const text = roles?.Text.Hex ?? null
  const defaultTheme = set?.Themes.find(theme => theme.Id === set.DefaultThemeId)
  const canPickStyle = ownTypeGroups(set?.Type?.Groups).some(group => group.Styles.length > 0)

  // A closed editor takes focus with it, so it goes back to that role's Change button unless the person moved on.
  const section = useRef<HTMLElement>(null)
  const openRole = useRef<BrandTokenRole | null>(null)
  useEffect(() => {
    const closed = openRole.current
    openRole.current = editor?.role ?? null
    if (editor === null && closed !== null && (document.activeElement === null || document.activeElement === document.body)) {
      section.current?.querySelector<HTMLButtonElement>(`[data-change="${closed}"]`)?.focus()
    }
  }, [editor?.role])

  const tile = (role: BrandTokenRole, changeable: boolean) => ({
    role,
    editing: editor?.role === role,
    change: canChange && changeable
      ? <ChangeButton role={role} editing={editor?.role === role}
          disabled={props.pending || props.entryWaiting !== null || (editor !== null && editor.role !== role)} onEdit={() => props.onEdit(role)} />
      : null,
    form: editor?.role === role ? <RoleEditorForm {...props} editor={editor} /> : null,
  })

  return (
    <section ref={section} aria-labelledby="bg-uses-h"
      className="px-6 sm:px-8 pt-12 pb-14 border-y border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 flex flex-col gap-7">
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6 lg:gap-10">
        <div className="min-w-0 flex flex-col gap-1.5">
          <h2 id="bg-uses-h" className={H2}>What OrbitalX uses</h2>
          <p className={LEDE}>
            {defaultTheme
              ? `Each role points at a token in this system and uses its ${defaultTheme.Name} value.`
              : 'Emails, landing pages and asset QA read these roles.'}
          </p>
        </div>
        <ColorsState {...props} />
      </div>

      <div className="flex flex-col gap-11">
        <div className="flex flex-col gap-3.5">
          <h3 className={EYEBROW}>Colors</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-x-7 gap-y-9 items-start">
            {COLOR_ROLES.map(({ role, usage }) => (
              <ColorTile key={role} {...tile(role, true)} resolved={roles?.[role] ?? null} usage={roleUsage(set, role, usage)}
                preview={editor?.role === role ? previewHex(set, editor) : null} />
            ))}
            <ContrastTile background={background} text={text} bodyStack={roles?.Body.FamilyStack ?? null} />
          </div>
        </div>

        <div className="flex flex-col gap-3.5">
          <h3 className={EYEBROW}>Type, radius and shadow</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-x-7 gap-y-9 items-start">
            {TYPE_ROLES.map(({ role, usage }) => (
              <TypeTile key={role} {...tile(role, canPickStyle)} resolved={roles?.[role] ?? null} usage={usage}
                ground={background} ink={text} font={fontState(props.fonts, roles?.[role].FamilyName)} />
            ))}
            {VALUE_ROLES.map(({ role, usage }) => (
              <ValueTile key={role} {...tile(role, true)} resolved={roles?.[role] ?? null} usage={roleUsage(set, role, usage)}
                ground={background} primary={roles?.Primary.Hex ?? null} />
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

/**
 * Where the colors stand (ORB-973). A change made after they were confirmed keeps the set confirmed, and says so;
 * confirming again, from the page's header, puts the new stamp on it.
 */
function ColorsState({ set }: Props) {
  if (set === null) return null
  const { Confirmed, ConfirmedByName, ConfirmedAt, Source } = set.Provenance
  const change = changedByLine(set.Provenance)
  if (Confirmed) {
    const by = ['Colors confirmed', ConfirmedByName ? `by ${ConfirmedByName}` : null].filter(Boolean).join(' ')
    const day = dayOf(ConfirmedAt)
    return (
      <div className="flex-none lg:w-[360px] flex flex-col items-start gap-3 pt-1">
        <p role="status" className="flex items-start gap-2 text-sm text-slate-600 dark:text-slate-300">
          <Check className="w-4 h-4 flex-none mt-0.5 text-green-600 dark:text-green-400" aria-hidden="true" />
          <span className="flex flex-col">
            <span>{day ? `${by} · ${day}.` : `${by}.`}</span>
            {change && (
              <span className={`font-medium ${change.byWebster ? 'text-purple-800 dark:text-purple-300' : 'text-slate-900 dark:text-white'}`}>
                {`${change.text}.`}
              </span>
            )}
            <span>Emails, landing pages and asset QA use these.</span>
          </span>
        </p>
      </div>
    )
  }
  return (
    <div className="flex-none lg:w-[360px] flex flex-col items-start gap-3 pt-1">
      <p className="flex items-start gap-2 text-sm text-slate-600 dark:text-slate-300">
        <Sparkles className="w-4 h-4 flex-none mt-0.5 text-purple-600 dark:text-purple-400" aria-hidden="true" />
        <span className="flex flex-col">
          <span className="font-medium text-purple-800 dark:text-purple-300">
            {Source === 'Website' ? 'Webster draft, not confirmed.' : 'Not confirmed.'}
          </span>
          <span>Emails and landing pages use these now. Asset QA uses them once a person confirms them.</span>
        </span>
      </p>
    </div>
  )
}

function fontState(fonts: Record<string, FontLoad>, family: string | null | undefined): FontLoad | null {
  return family ? fonts[family] ?? 'loading' : null
}

/** What a color tile shows while it is being changed: the typed hex once it is one, else the token chosen. */
function previewHex(set: BrandTokenSetViewDto | null, editor: RoleEditor): string | null {
  const typed = editor.literal.trim()
  if (/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(typed)) return typed
  if (!set?.DefaultThemeId || editor.token === '') return null
  return set.Colors.find(token => token.Name === editor.token)?.ResolvedHexByTheme[set.DefaultThemeId] ?? null
}

interface TileParts {
  role: BrandTokenRole
  editing: boolean
  change: ComponentChildren
  form: ComponentChildren
}

function ChangeButton({ role, editing, disabled, onEdit }: { role: string; editing: boolean; disabled: boolean; onEdit: () => void }) {
  if (editing) {
    return (
      <span className="flex-none inline-flex items-center gap-1 text-xs font-medium text-slate-500 dark:text-slate-400">
        <Pencil className="w-3 h-3" aria-hidden="true" />Editing
      </span>
    )
  }
  return (
    <button type="button" className={`${QUIET_BUTTON} -mr-1.5`} data-change={role} aria-label={`Change ${role}`} disabled={disabled} onClick={onEdit}>
      <Pencil className="w-3 h-3" aria-hidden="true" />Change
    </button>
  )
}

function Tile({ role, tokenName, sample, change, children }: {
  role: string
  tokenName: string
  sample: ComponentChildren
  change: ComponentChildren
  children: ComponentChildren
}) {
  return (
    <article aria-labelledby={`bg-role-${role}`} className="min-w-0 flex flex-col gap-3.5">
      {sample}
      <div className="flex flex-col gap-0.5">
        <div className="flex items-center justify-between gap-3 min-h-6">
          <h4 id={`bg-role-${role}`} className="text-sm font-semibold text-slate-900 dark:text-white">{role}</h4>
          {change}
        </div>
        <span className="font-mono text-xs text-slate-500 dark:text-slate-400">{tokenName}</span>
      </div>
      {children}
    </article>
  )
}

function Chain({ chain }: { chain: readonly string[] }) {
  if (chain.length === 0) return null
  return (
    <span className="min-w-0 truncate font-mono text-[13px] leading-5 text-slate-600 dark:text-slate-300">
      <span className="sr-only">Points at </span>
      <span aria-hidden="true" className="text-slate-400 dark:text-slate-500">&#8594; </span>{chain.join(' → ')}
    </span>
  )
}

function Missing({ problem }: { problem: string | null | undefined }) {
  return (
    <p className="flex items-center gap-1.5 text-sm font-medium text-amber-700 dark:text-amber-400">
      <TriangleAlert className="w-4 h-4 flex-none" aria-hidden="true" />{problemText(problem)}
    </p>
  )
}

function Usage({ children }: { children: ComponentChildren }) {
  return <p className="text-sm text-slate-600 dark:text-slate-300">{children}</p>
}

const EMPTY_SAMPLE = 'block h-24 rounded-lg border-2 border-dashed border-amber-300 dark:border-amber-500/60'

function ColorTile({ role, editing, change, form, resolved, usage, preview }: TileParts & {
  resolved: BrandColorRoleViewDto | null
  usage: string
  preview: string | null
}) {
  const hex = resolved?.Hex ?? null
  const shown = preview ?? hex
  // Both fill buttons in their own descriptions, and a button's label is usually white, so a color too pale to carry it
  // is said where it is chosen rather than found on a built page.
  const onWhite = (role === 'Primary' || role === 'Accent') && hex ? contrastRatio('#FFFFFF', hex) : null
  return (
    <Tile role={role} tokenName={resolved?.TokenName ?? `orbitalx-${role.toLowerCase()}`} change={change}
      sample={<span aria-hidden="true" className={shown ? `block h-24 ${SWATCH_FRAME}` : EMPTY_SAMPLE} style={shown ? { background: shown } : undefined} />}>
      {editing ? form : (
        <div className="flex flex-col gap-1.5">
          {hex ? (
            <div className="flex items-center gap-2">
              <Chain chain={resolved?.AliasChain ?? []} />
              <span className="flex-1" />
              <span className="font-mono text-[13px] leading-5 font-medium text-slate-900 dark:text-white">{hex}</span>
              <CopyButton text={hex} label={hex} />
            </div>
          ) : <Missing problem={resolved?.Problem} />}
          <Usage>{usage}</Usage>
          {onWhite !== null && onWhite < 4.5 && (
            <p className="flex items-start gap-1.5 text-sm text-amber-700 dark:text-amber-400">
              <TriangleAlert className="w-4 h-4 flex-none mt-0.5" aria-hidden="true" />
              <span>White text {formatRatio(onWhite)}, {onWhite >= 3 ? 'large text only' : 'too low for text'}</span>
            </p>
          )}
        </div>
      )}
    </Tile>
  )
}

const GRADE_CLASSES: Record<ReturnType<typeof contrastGrade>['tone'], string> = {
  g: BRAND_CLASSES.STATUS.SUCCESS,
  a: BRAND_CLASSES.STATUS.WARNING,
  r: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
}

function ContrastTile({ background, text, bodyStack }: { background: string | null; text: string | null; bodyStack: string | null }) {
  const ratio = contrastRatio(text, background)
  const grade = ratio === null ? null : contrastGrade(ratio)
  const missing = [background ? null : 'Background', text ? null : 'Text'].filter((role): role is string => role !== null)
  return (
    <article aria-labelledby="bg-role-contrast" className="min-w-0 flex flex-col gap-3.5">
      <span aria-hidden="true" className={background ? `flex items-center gap-3.5 h-24 px-5 ${SWATCH_FRAME}` : EMPTY_SAMPLE}
        style={background ? { background, color: text ?? undefined, fontFamily: bodyStack ?? undefined } : undefined}>
        {background && (
          <>
            <span className="text-[32px] leading-none">Aa</span>
            <span className="text-base leading-relaxed">Every email reads like this.</span>
          </>
        )}
      </span>
      <div className="flex flex-col gap-0.5">
        <div className="flex items-center min-h-6">
          <h4 id="bg-role-contrast" className="text-sm font-semibold text-slate-900 dark:text-white">Text on Background</h4>
        </div>
        {ratio !== null && grade !== null && (
          <div className="flex items-center gap-2.5">
            <span className="font-mono text-[13px] leading-5 font-medium text-slate-900 dark:text-white">{formatRatio(ratio)}</span>
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${GRADE_CLASSES[grade.tone]}`}>
              {grade.tone === 'g' ? <Check className="w-3 h-3" aria-hidden="true" /> : <TriangleAlert className="w-3 h-3" aria-hidden="true" />}
              {grade.label}
            </span>
          </div>
        )}
      </div>
      {missing.length === 0
        ? <Usage>Emails need Background and Text. Both are set.</Usage>
        : (
          <p className="flex items-start gap-1.5 text-sm text-amber-700 dark:text-amber-400">
            <TriangleAlert className="w-4 h-4 flex-none mt-0.5" aria-hidden="true" />
            <span>Emails need Background and Text. {missing.join(' and ')} {missing.length === 1 ? 'is' : 'are'} missing.</span>
          </p>
        )}
    </article>
  )
}

function TypeTile({ role, editing, change, form, resolved, usage, ground, ink, font }: TileParts & {
  resolved: BrandTypeRoleViewDto | null
  usage: string
  ground: string | null
  ink: string | null
  font: FontLoad | null
}) {
  const hasStyle = resolved?.StyleName != null
  const failed = font === 'failed'
  const caption = [resolved?.FamilyName ?? (hasStyle ? 'System font' : null), sizeOverLine(resolved?.FontSize, resolved?.LineHeight), resolved?.FontWeight]
    .filter(Boolean)
    .join(' · ')
  const sample = hasStyle ? (
    <span aria-hidden="true" className={`flex items-center h-24 px-5 overflow-hidden ${SWATCH_FRAME}`}
      style={{ background: ground ?? '#FFFFFF', color: ink ?? '#0F172A' }}>
      <span className="truncate" style={{
        fontFamily: failed ? systemFallback(resolved?.FamilyStack) : resolved?.FamilyStack ?? undefined,
        fontSize: resolved?.FontSize ?? undefined,
        lineHeight: resolved?.LineHeight ?? undefined,
        fontWeight: resolved?.FontWeight ?? undefined,
      }}>
        {role === 'Heading' ? 'Aa' : 'Paragraphs read in this face, at this size.'}
      </span>
    </span>
  ) : <span aria-hidden="true" className={EMPTY_SAMPLE} />
  return (
    <Tile role={role} tokenName={resolved?.TokenName ?? `orbitalx-${role.toLowerCase()}`} change={change} sample={sample}>
      {editing ? form : (
        <div className="flex flex-col gap-1.5">
          {hasStyle ? (
            <div className="flex flex-col gap-0.5">
              <Chain chain={resolved?.AliasChain ?? []} />
              <span className="text-sm font-medium text-slate-900 dark:text-white">{caption}</span>
              {failed && <span className="text-xs text-slate-500 dark:text-slate-400">Shown in a system font</span>}
            </div>
          ) : <Missing problem={resolved?.Problem} />}
          <Usage>{usage}</Usage>
        </div>
      )}
    </Tile>
  )
}

function ValueTile({ role, editing, change, form, resolved, usage, ground, primary }: TileParts & {
  resolved: BrandValueRoleViewDto | null
  usage: string
  ground: string | null
  primary: string | null
}) {
  const value = resolved?.Value ?? null
  const sample = value ? (
    <span aria-hidden="true" className={`flex items-center justify-center h-24 ${SWATCH_FRAME}`} style={{ background: ground ?? '#FFFFFF' }}>
      {role === 'Radius'
        ? <span className="block w-20 h-12" style={{ borderRadius: value, ...radiusFill(primary, ground) }} />
        : <span className="block w-[88px] h-11 rounded-lg" style={{ background: '#FFFFFF', boxShadow: value }} />}
    </span>
  ) : <span aria-hidden="true" className={EMPTY_SAMPLE} />
  return (
    <Tile role={role} tokenName={resolved?.TokenName ?? `orbitalx-${role.toLowerCase()}`} change={change} sample={sample}>
      {editing ? form : (
        <div className="flex flex-col gap-1.5">
          {value ? (
            <div className="flex flex-col gap-0.5 min-w-0">
              <Chain chain={resolved?.AliasChain ?? []} />
              <span className="flex items-center gap-1.5 min-w-0">
                <span className={`${role === 'Shadow' ? 'font-mono text-xs break-all' : 'text-sm'} font-medium text-slate-900 dark:text-white`}>{value}</span>
                {role === 'Shadow' && <CopyButton text={value} label="Shadow" />}
              </span>
            </div>
          ) : <Missing problem={resolved?.Problem} />}
          <Usage>{usage}</Usage>
        </div>
      )}
    </Tile>
  )
}

/** The radius sample in the brand's own primary, so a corner reads the way a button will. Neutral when it has none. */
function radiusFill(primary: string | null, ground: string | null): Record<string, string> {
  const tint = brandTint(primary, ground)
  return Object.keys(tint).length > 0 ? tint : { background: '#E2E8F0', boxShadow: 'inset 0 0 0 1.5px #64748B' }
}

const DANGER_SMALL = 'inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-lg bg-red-600 hover:bg-red-700 text-white transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed'

function RoleEditorForm({ set, editor, pending, onEditorChange, onCancel, onSave, onRemove }: Props & { editor: RoleEditor }) {
  const roles = set?.Roles ?? null
  const { role } = editor
  const current = currentRoleValue(roles, role)
  const target = roles?.[role].AliasChain[0] ?? null
  const typeRole = isTypeRole(role)
  const valueRole = isValueRole(role)
  const family: readonly { Name: string }[] = role === 'Radius' ? set?.Radius ?? [] : role === 'Shadow' ? set?.Shadow ?? [] : set?.Colors ?? []
  const options = typeRole
    ? ownTypeGroups(set?.Type?.Groups).flatMap(group => group.Styles.map(style => ({ value: style.Name, label: `${style.Name} (${group.Name})` })))
    : ownTokens(family).map(token => ({ value: token.Name, label: token.Name }))
  const picks = options.length > 0
  const error = editor.error ?? undefined
  const update = (patch: Partial<RoleEditor>) => onEditorChange({ ...editor, ...patch, error: null })
  const literalLabel = valueRole ? (picks ? 'Or a value' : 'Value') : (picks ? 'Or a hex value' : 'Hex value')
  const shownValue = isValueRole(role) ? roles?.[role].Value : isTypeRole(role) ? null : roles?.[role].Hex

  // The Change button that opened this form is replaced by it, so focus moves into the form.
  const form = useRef<HTMLFormElement>(null)
  useEffect(() => {
    form.current?.querySelector<HTMLElement>('select, input')?.focus()
  }, [role])

  return (
    <form ref={form} noValidate aria-label={`Change ${role}`} className="flex flex-col gap-3"
      onSubmit={event => { event.preventDefault(); if (!editor.removing) onSave() }}>
      {picks && (
        <Select name="bg-role-token" label={typeRole ? 'Use a style from this system' : 'Use a token from this system'}
          density="compact" options={options} placeholder={typeRole ? 'Choose a style' : 'Choose a token'}
          value={editor.token} disabled={pending} error={typeRole ? error : undefined}
          onChange={(event: Event) => update({ token: (event.target as HTMLSelectElement).value })} />
      )}
      {!typeRole && (
        <TextBox name="bg-role-value" label={literalLabel} density="compact" value={editor.literal} disabled={pending}
          placeholder={shownValue ?? (valueRole ? '' : '#0B1F3A')} error={error}
          helperText={target ? `A value typed here replaces the link to ${target}.` : undefined}
          onChange={literal => update({ literal })} />
      )}
      {editor.removing ? (
        <div className="flex flex-col gap-2">
          <p className="text-sm text-slate-700 dark:text-slate-300">{removeRoleQuestion(role)}</p>
          <div className="flex items-center gap-2">
            <button type="button" className={DANGER_SMALL} disabled={pending} onClick={onRemove}>Remove</button>
            <button type="button" className={GHOST_SMALL} disabled={pending} onClick={() => update({ removing: false })}>Keep</button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <button type="submit" className={SAVE_SMALL} disabled={pending || !roleEditorChanged(roles, editor)}>
            {pending ? 'Saving...' : 'Save'}
          </button>
          <button type="button" className={GHOST_SMALL} disabled={pending} onClick={onCancel}>Cancel</button>
          {current !== '' && (
            <button type="button" disabled={pending} onClick={() => update({ removing: true })}
              className="ml-auto text-sm font-medium text-red-600 dark:text-red-400 hover:underline disabled:opacity-50">
              Remove {role}
            </button>
          )}
        </div>
      )}
    </form>
  )
}
